import type {FC} from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  Typography,
} from '@wellstone-solutions/web';
import {toAnswers} from '../../state';
import type {
  FeedbackErrorReason,
  FeedbackForm,
  FeedbackSubmission,
} from '../../types';
import {useFeedbackFormState} from '../../useFeedbackFormState';
import {QuestionField} from './QuestionField';

type FeedbackModalProps = {
  form: FeedbackForm;
  signalHeadline: string;
  firstName: string;
  isSubmitting: boolean;
  errorReason?: FeedbackErrorReason;
  onCancel: () => void;
  onSubmit: (submission: FeedbackSubmission) => void;
};

export const FeedbackModal: FC<FeedbackModalProps> = ({
  form,
  signalHeadline,
  firstName,
  isSubmitting,
  errorReason,
  onCancel,
  onSubmit,
}) => {
  const formState = useFeedbackFormState(form.definition);
  const incomplete = Object.keys(formState.errors).length > 0;

  const handleSend = () => {
    const state = formState.submit();
    if (!state) return;
    onSubmit({formId: form.id, answers: toAnswers(form.definition, state)});
  };

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onCancel}>
      <DialogTitle>
        <Typography component="div" sx={{fontSize: 20, fontWeight: 700}}>
          Signal feedback for {firstName}
        </Typography>
        <Typography sx={{mt: 0.75, fontSize: 14, fontWeight: 600}}>
          {signalHeadline}
        </Typography>
        <Typography sx={{mt: 1, fontSize: 14, color: 'text.secondary'}}>
          Your answers help us tune future signals. Sending feedback doesn't
          change their review status.
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{bgcolor: 'grey.50'}}>
        {errorReason === 'gone' && (
          <Alert severity="warning" sx={{mb: 2}}>
            This signal is no longer available.
          </Alert>
        )}
        {errorReason === 'generic' && (
          <Alert severity="error" sx={{mb: 2}}>
            We couldn't send your feedback. Please try again.
          </Alert>
        )}
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            color: 'primary.main',
          }}>
          How did this signal land?
        </Typography>
        {form.definition.questions.map((question, index) => (
          <QuestionField
            key={question.id}
            question={question}
            form={formState}
            divider={index > 0}
          />
        ))}
      </DialogContent>
      <DialogActions sx={{px: 3, py: 1.75}}>
        <Typography
          sx={{flex: '1 1 auto', fontSize: 13, color: 'text.secondary'}}>
          {incomplete ? 'Answer the required questions to send.' : ''}
        </Typography>
        <Button variant="text" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isSubmitting || incomplete}
          onClick={handleSend}
          startIcon={<Icon name="SendDiagonal" size={18} />}>
          Send feedback
        </Button>
      </DialogActions>
    </Dialog>
  );
};
